import { create } from 'zustand';
import { errorMessage } from '../services/errors';
import { DARK_QUERY, THEME_STORAGE_KEY, bootTheme } from './theme';
import type { ThemeChoice, ThemeReading } from './theme';

interface ThemeState extends ThemeReading {
  /** Why the last choice was not saved; null when it was. */
  saveError: string | null;
  choose: (choice: ThemeChoice) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  ...bootTheme(THEME_STORAGE_KEY, DARK_QUERY),
  saveError: null,
  choose: (choice) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch (error) {
      // Not applied either: the page keeps what a reload would show.
      set({ saveError: errorMessage(error) });
      return;
    }
    set({ saveError: null });
    refreshTheme();
  },
}));

/** Reads the saved choice again and puts it on the page, the browser's toolbar color with it. */
export function refreshTheme(): void {
  useTheme.setState(bootTheme(THEME_STORAGE_KEY, DARK_QUERY));
  // index.css names the toolbar color per theme; before the stylesheet has loaded there is none to give.
  const chrome = getComputedStyle(document.documentElement).getPropertyValue('--chrome').trim();
  if (!chrome) return;
  // index.html has one per device mode; the chosen theme sets both, so a Light or Dark choice wins over the device's mode.
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((meta) => {
    meta.content = `rgb(${chrome})`;
  });
}

/**
 * Keeps the page on the chosen theme while it is open: follows the device's
 * dark mode as it changes (it matters only while System is chosen), and a
 * choice made in another tab. Call once, at start; returns the unsubscribe.
 */
export function followTheme(): () => void {
  const media = window.matchMedia(DARK_QUERY);
  const onStorage = (event: StorageEvent) => {
    // A null key is the other tab clearing all of storage.
    if (event.key === THEME_STORAGE_KEY || event.key === null) refreshTheme();
  };
  media.addEventListener('change', refreshTheme);
  window.addEventListener('storage', onStorage);
  refreshTheme();
  return () => {
    media.removeEventListener('change', refreshTheme);
    window.removeEventListener('storage', onStorage);
  };
}
